import { clsx } from "clsx";
import { type ReactNode } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { ActionButton, LoadingSpinner } from "widgets";

import "../styles/_errors-page.scss";

import style from "../styles/InformationPage.module.scss";

import { supportedBrowsersList } from "../../../app-utils/browser-compatibility.js";
import type { Configuration } from "@/graphql.ts";
import type { IAppState } from "@/redux/reducer.ts";

export interface IErrorPageProps {
  errorCode?: number;
  errorTitle: string;
  children?: ReactNode | string;
  noGoBackLink?: boolean;
  showReloadLink?: boolean;
  helpdeskEmail?: string;
}

const error401: IErrorPageProps = {
  errorCode: 401,
  errorTitle: "Unauthorized",
  children: (
    <p>
      You have no permissions to access this page.
      <br />
      If you think this is an error, please contact your system administrator.
    </p>
  ),
};

const error404: IErrorPageProps = {
  errorCode: 404,
  errorTitle: "Not found",
  children: <p>Sorry, the page you are looking for does not exist.</p>,
};

const error500: IErrorPageProps = {
  errorCode: 500,
  errorTitle: "Internal error",
  children: <p>Sorry, something went wrong. Please report this problem.</p>,
};

const notImplemented: IErrorPageProps = {
  errorCode: 999,
  errorTitle: "Not yet implemented",
  children: <p>Sorry, the resource you are looking for is not yet implemented.</p>,
};

const browserUnsupported: IErrorPageProps = {
  errorTitle: "Unsupported Browser",
  children: (
    <>
      <p>
        Sorry, this browser is not supported by Genedata Selector. Please use one of the browser versions listed below:
      </p>
      {supportedBrowsersList()}
    </>
  ),
  noGoBackLink: true,
};

const limitedBrowserSupport = ({ helpdeskEmail }: Pick<IErrorPageProps, "helpdeskEmail">): IErrorPageProps => {
  // show email support only if e-mail is set.
  let emailNode = null;

  if (helpdeskEmail && typeof helpdeskEmail === "string" && helpdeskEmail.trim().length > 0) {
    emailNode = (
      <p>
        If you have questions about web browser support, please send an email to{" "}
        <a href={`mailto:${helpdeskEmail}`}>{helpdeskEmail}</a>.
      </p>
    );
  }

  return {
    errorTitle: "Limited Support",
    children: (
      <>
        <p>
          You are using a web browser that is not fully supported by this application. If you experience problems or
          slow performance, please switch to one of the following web browsers:
        </p>
        {supportedBrowsersList(false)}
        {emailNode}
      </>
    ),
  };
};

const dataInProgress: IErrorPageProps = {
  errorTitle: null, // no title
  noGoBackLink: true,
  children: (
    <div>
      Preparing the data for you.
      <br />
      <br />
      <LoadingSpinner />
    </div>
  ),
};

export default function InformationPage(props: IErrorPageProps = error404) {
  const navigate = useNavigate();

  return (
    <div className={clsx(style.information_page, "information-page")} data-testid={"error-page"}>
      {props.errorCode && <h1 className="error-number">{props.errorCode}</h1>}
      <h2 className="semi-bold">{props.errorTitle}</h2>
      {props.children}
      {!props.noGoBackLink && !props.showReloadLink && (
        <ActionButton
          label={"Go Back"}
          onPress={() => {
            history ? navigate(-1) : window.history.back();
          }}
        />
      )}
    </div>
  );
}

export function ErrorPage401() {
  return InformationPage({ ...error401 });
}

// props contain the RoutingProperties, might be needed for triggering the BACK link
export function ErrorPage404() {
  return InformationPage({ ...error404 });
}

export function ErrorPage500() {
  return InformationPage({ ...error500 });
}

export function NotImplementedPage() {
  return InformationPage({ ...notImplemented });
}

export function BrowserUnsupportedPage() {
  return InformationPage({ ...browserUnsupported });
}

export function LimitedBrowserSupportPage() {
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration);

  return InformationPage({ ...limitedBrowserSupport({ helpdeskEmail: configuration?.application?.helpdeskEmail }) });
}

export function DataInProgessPage() {
  return InformationPage({ ...dataInProgress });
}
